import { useMemo, useRef, useState, type MouseEvent, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ScaleChip, ScaleDataGrid, ScalePagination, ScaleTextField } from '@cloudorder/ui';

import type { TagColor } from '@/shared/ui/tone';

import { headerWidth, actionsWidth, settle, tagsWidth, textWidth } from './columnWidth';
import styles from './DataGrid.module.css';

/* The cockpit's tables, drawn by Scale.
 *
 * scale-data-grid owns the table itself: sorting, pagination, column visibility,
 * column resizing and the card layout it falls back to on a narrow screen. It
 * takes `fields` — one descriptor per column — and `rows` as arrays of plain
 * values, one value per field, rendered by a fixed registry of cell types. That
 * is the whole contract, and it is why this file exists: a screen should say
 * "this column is a status chip" and not have to remember that a status chip
 * means a `tags` field carrying an array of `{ content, color }`.
 *
 * Two things the grid does not do, which this adapter adds:
 *
 * - A toolbar. Search and scope filters go into the grid's own title-block slot
 *   rather than floating above it, so narrowing the view happens inside the
 *   control it narrows.
 * - An empty state. Given no rows the grid renders nothing at all, and a table
 *   that vanishes reads as a bug rather than as an answer.
 *
 * One constraint worth knowing before adding a column. The grid picks its sort
 * comparator from the cell type and applies it to the raw cell value, so a
 * `link`, `tags` or `action` column — whose value is an object or an array —
 * cannot sort; the header would toggle and nothing would move. Those kinds are
 * forced unsortable here rather than left to look sortable and quietly fail.
 * Where sorting by such a dimension matters, offer it as a filter instead. */

export interface GridTag {
  readonly content: string;
  readonly color?: TagColor;
}

export interface GridLink {
  readonly text: string;
  readonly href: string;
}

/** The row count used by both Scale's local pager and service-backed grid pages. */
export const GRID_PAGE_SIZE = 10;

/** One button in an action column.
 *
 * Deliberately narrow. A row action is a verb the operator can apply to that
 * row and nothing more — no href, no arbitrary button props. `ariaLabel`
 * exists for icon-only labels so the visual affordance can stay compact
 * without dropping accessible naming.
 */
export interface GridAction {
  readonly label: string;
  readonly ariaLabel?: string;
  readonly iconName?: 'edit' | 'delete' | 'launch' | 'power';
  readonly iconOnly?: boolean;
  /** Inline HTML rendered as the button's content instead of `label`. Use for
      icon-only actions whose icon is not in the grid's built-in icon registry.
      The value should be a single Scale icon element, e.g.
      `'<scale-icon-action-launch size="16" decorative></scale-icon-action-launch>'` */
  readonly htmlLabel?: string;
  readonly tooltip?: string;
  /** Greyed and inert. Use it for an action that does not apply to this row,
      rather than omitting the button: a column whose buttons move around as you
      read down it is harder to use than one with a dead key in it. */
  readonly disabled?: boolean;
  readonly variant?: 'primary' | 'secondary' | 'ghost';
  readonly onSelect: () => void;
}

interface Shared {
  readonly header: string;
  /** Sortable by default wherever the grid can actually sort it. */
  readonly sortable?: boolean;
  /** Start hidden and let the grid's column menu reveal it. For the detail a
      row carries but does not lead with — a tenant id, a subscription. */
  readonly visible?: boolean;
  readonly width?: number;
  /** The one column that titles the card when the grid goes to its narrow
      layout. At most one per grid; the grid takes the first it finds. */
  readonly mobileTitle?: boolean;
  /** Sort by this column on first render. Requires the column to be sortable. */
  readonly presort?: 'ascending' | 'descending';
}

export type GridColumn<T> =
  | (Shared & { readonly kind: 'text'; readonly value: (row: T) => string })
  | (Shared & {
      readonly kind: 'number';
      readonly value: (row: T) => number;
      readonly precision?: number;
      readonly suffix?: string;
    })
  | (Shared & {
      readonly kind: 'date';
      readonly value: (row: T) => string;
      /** A date-fns pattern matching what `value` returns, e.g. `dd MMM HH:mm`.
          Supply it whenever the printed form does not sort alphabetically into
          chronological order, which is nearly always: without it the grid falls
          back to sorting the text, and "3 Sep" lands before "12 Aug". */
      readonly format?: string;
    })
  | (Shared & { readonly kind: 'link'; readonly value: (row: T) => GridLink })
  | (Shared & { readonly kind: 'tags'; readonly value: (row: T) => readonly GridTag[] })
  /** A 0–100 figure drawn as a track with its percentage beside it. The cell
      prints the % sign itself, so the header should not repeat it. */
  | (Shared & { readonly kind: 'meter'; readonly value: (row: T) => number })
  /** Buttons that act on the row. Never sortable, and never the mobile title:
      there is no order to put "Retire" in. */
  | (Shared & { readonly kind: 'action'; readonly value: (row: T) => readonly GridAction[] })
  /** A boolean cell rendered as a Scale switch. `editable` makes the switch
      interactive; omit or set false for a read-only indicator. The value
      function returns the checked state; `onChange` is called when the operator
      toggles an editable switch. */
  | (Shared & {
      readonly kind: 'checkbox';
      readonly style: 'switch';
      readonly editable?: boolean;
      readonly sortable?: boolean;
      readonly value: (row: T) => boolean;
      readonly onChange?: (row: T, checked: boolean) => void;
    });

export interface GridFilter<T> {
  readonly key: string;
  readonly label: string;
  readonly test: (row: T) => boolean;
}

/**
 * Hands search and paging back to whoever supplied the rows.
 *
 * Absent — which is every table in the cockpit but two — the grid does what it
 * has always done: it holds every row, narrows them in the browser, and lets
 * scale-data-grid page what is left. That is right for a queue of forty cases
 * and it is right for a configuration table, and nothing about those changes.
 *
 * Present, it means the rows on `rows` are one page of an answer that lives in
 * the service, and the three things the grid cannot do without the rest of that
 * answer are delegated: the search term is the screen's state because the
 * service applies it, the pager reports `total` rather than the page's length,
 * and moving between pages is a request rather than a slice. Sorting is the one
 * thing that genuinely goes: a header that reordered the twenty rows on screen
 * while six hundred sat behind them would be lying about what it sorted, so the
 * screens that turn this on pass `sortable: false` and offer the ordering the
 * service applies instead.
 *
 * A scope chip changes hands the same way and for the same reason, through
 * `scope` and `onScope` — a chip tested in the browser would narrow the page
 * rather than the answer. A screen that passes `filters` alongside `paging` must
 * supply both, and the chip's own `test` is then never called; a screen whose
 * service has no such parameter drops the chip instead, because a scope that
 * silently means "of the hundred on screen" is worse than no scope at all.
 */
export interface GridPaging {
  /** How many rows match, of which `rows` is one page. */
  readonly total: number;
  readonly offset: number;
  /** Rows per request. The pager steps by this; the service may return fewer. */
  readonly limit: number;
  readonly onOffset: (offset: number) => void;
  /** The search term, owned by the screen because the service is what applies
      it. Supply it together with `search` on the grid, or the box appears and
      types into nothing. */
  readonly term: string;
  readonly onTerm: (term: string) => void;
  /** The chosen scope's key, or null for "all". Required alongside `filters`. */
  readonly scope?: string | null;
  readonly onScope?: (key: string | null) => void;
}

interface Props<T> {
  readonly columns: readonly GridColumn<T>[];
  readonly rows: readonly T[];
  /** Labels the grid for a screen reader, since the table lives in a shadow
      root where a <caption> of ours could not reach it. */
  readonly caption: string;
  /** Everything a row can be searched on, joined. Omit to hide the search box.
      Under `paging` the function is never called — the service has already
      matched — but it is still what says a search box belongs here. */
  readonly search?: (row: T) => string;
  readonly searchPlaceholder?: string;
  /** Scopes offered in the toolbar. One at a time: the useful ones are
      alternatives to each other, and combining them mostly yields nothing.
      Alongside `paging`, the chosen scope is the screen's — see `GridPaging` —
      and `test` is never called. */
  readonly filters?: readonly GridFilter<T>[];
  readonly controls?: ReactNode;
  /** Rows per page. The grid shows its pager only once the rows exceed this,
      so a short configuration table stays a plain list. Ignored under `paging`,
      where the page size is the service's to decide. */
  readonly pageSize?: number;
  /** Turns search and paging over to the caller. See `GridPaging`. */
  readonly paging?: GridPaging;
  readonly emptyTitle?: string;
  readonly empty?: string;
}

const cellTypes = {
  text: 'text',
  number: 'number',
  date: 'date',
  link: 'link',
  tags: 'tags',
  meter: 'graph',
  action: 'actions',
  checkbox: 'checkbox',
} as const;

/* Row density, taken from Scale's own table rather than chosen here.
 *
 * scale-table draws a body cell as 8px of padding with no margin, separates
 * rows with a hairline, and lets the cell inherit the page's font size. The
 * data grid, for no stated reason, gives the same cell 8px of margin *and* 8px
 * of padding, draws no separator at all, and pins its shadow host to Scale's
 * `body` step while the cockpit runs at `caption` — three times the chrome and
 * a row you have to find by eye. This restates the table's numbers on the grid,
 * which is what the grid's `styles` prop is for: it renders as a <style> inside
 * the shadow root, and it is the only supported way in, since the grid exposes
 * parts for its title and scroll container and nothing else.
 *
 * Vertical properties only, and deliberately so. `updateColumnStretching` adds
 * a hard-coded 32px per column for "padding+margin" and carries a note that any
 * horizontal change has to be mirrored there; the first cell's `margin-left` is
 * left alone for the same reason. Selectors are scoped through `.data-grid` to
 * outrank the component's own rules, which Stencil loads as adopted stylesheets
 * and the cascade therefore applies *after* this one, and `:not(--mobile)`
 * keeps the below-500px card layout — which sets its own padding and border —
 * exactly as Scale drew it. */
const density = `
.data-grid:not(.data-grid--mobile) .tbody__cell {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: var(--telekom-spacing-composition-space-04);
  padding-bottom: var(--telekom-spacing-composition-space-04);
  vertical-align: middle;
}
/* The table borders every row; the grid has an info footer with a border of its
   own underneath the last one. */
.data-grid:not(.data-grid--mobile) .tbody__row:not(:last-of-type) {
  border-bottom: var(--telekom-spacing-composition-space-01) solid
    var(--telekom-color-ui-faint);
}
.data-grid:not(.data-grid--mobile) .tbody__row:hover {
  background: var(--telekom-color-ui-state-fill-hovered);
}
/* The grid's :host pins body size and Scale's standard leading; scale-table sets
   neither and simply inherits the page. These are the two values the body rule
   in cloudorder.css carries — restated because a shadow root cannot inherit past
   a host that has already declared them. Custom properties do cross the
   boundary, so the tokens themselves resolve here as they do anywhere. */
.data-grid {
  font-size: var(--co-size-caption);
  line-height: var(--co-leading-standard);
}
/* Scale's text, date and number cells wrap their value in a p.scl-body, whose
   font shorthand hard-sets the 16px body step over the row's own 14px. */
.data-grid .scl-body {
  font: inherit;
}
/* The meter cell, on one line.
 *
 * The bar stacks its percentage above the track, which then runs the full column
 * width. Making the base a flex row and pulling the wrapper ahead of the number
 * puts them side by side. Scale replaces "100%" with a narrower success icon,
 * so the wrapper always reserves the same 34px status column to keep every
 * track the same width.
 *
 * The base part is named after the component, not "base". And selectors after
 * ::part() are illegal, so the empty span Scale renders in place of a missing
 * label is unreachable; it supplies one space of separation, hence no gap. */
.data-grid .data-grid-progress-bar::part(progress-bar) {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
/* Reserve the status column even when Scale replaces the percentage with its
   narrower completion icon. */
.data-grid .data-grid-progress-bar::part(wrapper) {
  order: -1;
  flex: 0 1 calc(100% - 34px);
  width: calc(100% - 34px);
  min-width: 0;
}
.data-grid .data-grid-progress-bar::part(status) {
  padding: 0;
  min-width: 34px;
  text-align: right;
}
/* The percentage draws in --telekom-text-style-ui — 16px, on a 14px row. */
.data-grid .data-grid-progress-bar {
  --font-label: var(--telekom-text-style-caption-bold);
}
/* The toolbar's own row.
 *
 * The title block is a flex row that ends at flex-end, and the slot arrives
 * inside a bare div carrying no class and no flex-grow — so a toolbar is pushed
 * against the settings menu and wraps rather than laying out across the header.
 * Letting that wrapper grow puts search and filters at the left, where the
 * table starts, and leaves the menu at the right where Scale put it. The block's
 * only other children are an h4 and a scale-menu-flyout, so the child selector
 * cannot pick up anything else. */
.data-grid .data-grid__title-block > div {
  flex: 1 1 auto;
  min-width: 0;
}
/* The block's own 24px of padding already lands on the first column's text —
   16px of cell margin and 8px of cell padding. The slotted margin would carry
   the toolbar 8px past it. */
.data-grid .data-grid__title-block ::slotted(*) {
  margin-left: 0;
}
/* Scale's table-options button is a button at its default size: 44px, beside a
   32px field and 32px chips. --min-height is what the component declares for
   this, so the small size is set rather than imitated. */
.data-grid .data-grid__settings-menu scale-button {
  --min-height: var(--telekom-spacing-composition-space-10);
}
/* Action-cell icon-only buttons.
 *
 * For htmlLabel buttons: scale-button with icon-only renders a square button
 * but the light-DOM innerHTML icon needs explicit centering and sizing.
 * For registered-icon buttons (edit/delete): the icon is a Stencil child,
 * also needing the same 16px cap.
 *
 * The inner button element (::part(button)) is the flex container; centering
 * there aligns the icon regardless of how Scale lays it out internally. */
.data-grid .tbody__actions scale-button scale-icon-action-launch,
.data-grid .tbody__actions scale-button scale-icon-action-power {
  display: block;
  width: 16px;
  height: 16px;
}
/* Center the light-DOM icon inside the button's shadow ::part(button). */
.data-grid .tbody__actions scale-button[icon-only]::part(button) {
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Action buttons that host a <scale-checkbox> as their htmlLabel render as a
   transparent wrapper so only the checkbox is visible. */
.data-grid .tbody__actions scale-button:has(scale-checkbox)::part(button) {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 0;
  min-height: 0;
  min-width: 0;
  outline-offset: 2px;
}
.data-grid .tbody__actions scale-button:has(scale-checkbox) {
  --border-width: 0;
  --border-style: none;
  --background: transparent;
  --background-hover: transparent;
  --background-active: transparent;
  --box-shadow: none;
  --color: inherit;
  min-height: 0;
}
`;

/* The search field, dense.
 *
 * Scale ships one text field, 44px tall, and its placeholder is transparent
 * until the input takes focus. That is fine under a floating label and useless
 * without one: hide the label — which a toolbar must, having nowhere to put it —
 * and the control renders as an empty box with no affordance at all.
 *
 * The prop that used to size a field is deprecated, carrying the note "css
 * overwrite should replace size", so this is the sanctioned route. The heights
 * are set from the custom properties Scale declares for them, in
 * DataGrid.module.css; what has no custom property goes through the field's own
 * styles injection, one class more specific than the rules it replaces.
 *
 * Unlike the grid, scale-text-field declares no encapsulation, so it renders in
 * the light DOM and both this and its own stylesheet are ordinary global CSS —
 * which is also why these rules can be written once and left: the cockpit has a
 * single text field, the one below. Should a second appear elsewhere with its
 * label hidden, it would pick these up too.
 *
 * The padding also loses 48px on the right, which the hide-label rule reserves
 * for an icon this field does not have.
 *
 * Exported because it is not really the grid's: it is what any dense field with
 * its label hidden needs, and the tenant chooser has one that renders before
 * this grid does. Two copies of these rules would be two answers to the same
 * question. */
export const searchFieldStyles = `
.text-field.text-field--hide-label .text-field__control {
  padding-top: 0;
  padding-bottom: 0;
  padding-right: var(--telekom-spacing-composition-space-05);
  font-size: var(--co-size-caption);
}
.text-field.text-field--hide-label .text-field__control::placeholder {
  color: var(--color-placeholder);
}
`;

/* The filter chips, at the cockpit's size.
 *
 * scale-chip declares a --font-size property it never reads; the label is a flat
 * `font-size: 1rem`, so a scope filter renders 16px beside a 14px table. Scoped
 * through `.chip` because Stencil's own sheet is adopted, and so applies after
 * this <style> rather than before it. */
const chipStyles = `
.chip .chip-label {
  font-size: var(--co-size-caption);
}
`;

/** The grid cannot sort an object or an array; see the note at the top. */
function canSort<T>(column: GridColumn<T>): boolean {
  if (column.kind === 'link' || column.kind === 'tags' || column.kind === 'action' || column.kind === 'checkbox') return false;
  return column.sortable !== false;
}

/** What a number cell prints, so it can be measured. Mirrors Scale's number
    cell, which formats to `precision` decimals and then appends the suffix. */
function printed(value: number, precision?: number, suffix?: string): string {
  const digits = precision === undefined ? String(value) : value.toFixed(precision);
  return suffix === undefined ? digits : digits + suffix;
}

/* The widest thing a column has to hold, measured off every row rather than the
   page on show — a column that resized as you paged would read as a fault. */
function contentWidth<T>(column: GridColumn<T>, rows: readonly T[]): number {
  switch (column.kind) {
    case 'tags':
      return tagsWidth(rows.map((row) => column.value(row)));
    case 'action':
      return actionsWidth(rows.map((row) => column.value(row)));
    case 'link':
      return textWidth(rows.map((row) => column.value(row).text));
    case 'number':
      return textWidth(rows.map((row) => printed(column.value(row), column.precision, column.suffix)));
    case 'meter':
      /* A progress bar reports only its own 50px minimum to any measurement, so
         a meter column is sized by neither its content nor its header. 128px
         holds the number and still leaves ~60px of track. */
      return 128;
    case 'checkbox':
      /* A switch cell is fixed-width; content measurement is meaningless. */
      return 120;
    default:
      return textWidth(rows.map((row) => column.value(row)));
  }
}

function fieldFor<T>(column: GridColumn<T>, rows: readonly T[]): Record<string, unknown> {
  const sortable = canSort(column);

  if (column.kind === 'checkbox') {
    return {
      type: 'checkbox',
      label: column.header,
      style: column.style,
      editable: column.editable === true,
      sortable: column.sortable === true,
      width: column.width ?? 120,
    };
  }

  const field: Record<string, unknown> = {
    type: cellTypes[column.kind],
    label: column.header,
    /* Wide enough for the header and every value beneath it. A column with a
       width set is excluded from the grid's auto-width pass, and stretching only
       ever adds to it, so this is a floor rather than a cap. */
    width: column.width ?? settle(headerWidth(column.header), contentWidth(column, rows)),
    sortable,
  };

  if (column.visible === false) field.visible = false;
  if (column.mobileTitle === true) field.mobileTitle = true;
  if (sortable && column.presort !== undefined) {
    field.presort = true;
    field.presortDirection = column.presort;
  }

  if (column.kind === 'meter') {
    /* `progress`, not `bar`. Both draw the same track, but `bar` adds a <p> with
       the raw number beside it *and* leaves the progress bar's own status
       switched on: graph-cell.js has `showStatus` commented out rather than set
       to false, and the property defaults to true. So a bar cell prints every
       figure twice — once as "41%" from the bar, once as "41" from the <p> —
       and then clips the second copy, because the column was sized without it.
       `progress` draws the track and exactly one number. */
    field.style = 'progress';
    field.min = 0;
    field.max = 100;
  }

  if (column.kind === 'number') {
    if (column.precision !== undefined) field.precision = column.precision;
    if (column.suffix !== undefined) field.suffix = column.suffix;
  }

  if (column.kind === 'date' && column.format !== undefined) {
    field.format = column.format;
  }

  return field;
}

function cellFor<T>(column: GridColumn<T>, row: T): unknown {
  if (column.kind === 'checkbox') return column.value(row);
  switch (column.kind) {
    case 'tags':
      // Spelled out rather than passed through, so a caller cannot reach the
      // rest of scale-tag's API from a column definition and quietly introduce
      // a second chip style. `standard` is the only type offered: it pairs each
      // colour with Scale's matching on-subtle text, where `strong` pins the
      // text to black on every fill.
      return column.value(row).map((tag) => ({
        content: tag.content,
        color: tag.color ?? 'standard',
        type: 'standard',
      }));
    case 'link': {
      const link = column.value(row);
      return { text: link.text, href: link.href };
    }
    case 'action':
      /* Spelled out for the same reason `tags` is: the cell spreads whatever it
         is given straight onto a scale-button, so passing a column's descriptor
         through unchanged would make every property of that component reachable
         from a table definition. `onClick` is the name Scale's cell binds — the
         column says `onSelect`, because from a column's side this is a choice
         being made, not a DOM event being handled. */
      return column.value(row).map((action) => ({
        /* When htmlLabel is supplied the action cell uses the `__html` path,
           rendering the HTML as the button's innerHTML — giving an icon-only
           button for icons outside the built-in registry. `icon-only` is
           spread onto scale-button so it sizes as a square rather than a
           full-width text button. When iconOnly is true with a registered
           iconName, an empty label string hides the text while the icon
           component is still slotted alongside it. */
        label: action.htmlLabel ? { __html: action.htmlLabel } : action.label,
        'aria-label': action.ariaLabel,
        iconName: action.htmlLabel ? undefined : action.iconName,
        /* `iconOnly` (camelCase) is the Stencil JS prop; the action cell's
           __rest spreads it directly onto h('scale-button'). This tells
           Scale to render a square button with centered icon and no text. */
        iconOnly: action.htmlLabel || action.iconOnly ? true : undefined,
        tooltip: action.tooltip,
        size: 'small',
        variant: action.variant ?? 'secondary',
        disabled: action.disabled === true,
        onClick: action.onSelect,
      }));
    default:
      return column.value(row);
  }
}

export function DataGrid<T>({
  columns,
  rows,
  caption,
  search,
  searchPlaceholder,
  filters,
  controls,
  pageSize = GRID_PAGE_SIZE,
  paging,
  emptyTitle,
  empty,
}: Props<T>): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ownTerm, setOwnTerm] = useState('');
  const [ownFilter, setOwnFilter] = useState<string | null>(null);

  /* Which of the two the toolbar reads and writes. Under `paging` the term and
     the scope are the screen's, because the service is what applies them;
     without it the grid keeps its own, because nobody else has any use for
     them. A scope change also returns to the first page, for the reason a
     keystroke does. */
  const term = paging ? paging.term : ownTerm;
  const setTerm = paging ? paging.onTerm : setOwnTerm;
  const served = paging?.onScope;
  const filter = served ? (paging?.scope ?? null) : ownFilter;

  function setFilter(key: string | null): void {
    if (served) {
      served(key);
      paging?.onOffset(0);
      return;
    }
    setOwnFilter(key);
  }

  /* Resolved here rather than as default parameters: `t` is only available once
     the component is running, and a screen that supplies its own wording has
     already translated it. */
  const placeholder = searchPlaceholder ?? t('grid.searchPlaceholder');
  const nothingTitle = emptyTitle ?? t('grid.emptyTitle');
  const nothingBody = empty ?? t('grid.empty');

  const active = filters?.find((candidate) => candidate.key === filter);

  const visible = useMemo(() => {
    /* Nothing to do under `paging`: the service returned the rows that match,
       and filtering them again here could only ever remove one it meant to
       send. */
    if (paging) return rows;
    const needle = term.trim().toLowerCase();
    let result = rows;
    if (active) result = result.filter((row) => active.test(row));
    if (search && needle) {
      result = result.filter((row) => search(row).toLowerCase().includes(needle));
    }
    return result;
  }, [rows, active, search, term, paging]);

  /* The widest each column has had to be, across every page seen so far.
   *
   * `contentWidth` measures the rows it is handed, which is the whole answer
   * when there is one and one page of it under `paging` — and a column that
   * resized as you paged would read as a fault, which is the note beside
   * `contentWidth` and the reason for this. Keeping the high-water mark makes
   * the widths monotonic: a page with a longer name widens the column and
   * nothing narrows it again, so the table settles instead of oscillating. Kept
   * in a ref rather than in state because widening is not a reason to render —
   * the render that measured it is already the one that will use it. */
  const widths = useRef(new Map<string, number>());

  const fields = useMemo(() => {
    return columns.map((column) => {
      const field = fieldFor(column, rows);
      if (!paging || column.width !== undefined) return field;
      const seen = widths.current.get(column.header) ?? 0;
      const width = Math.max(seen, Number(field.width));
      widths.current.set(column.header, width);
      return { ...field, width };
    });
  }, [columns, rows, paging]);

  /* Freshly built every time the visible set changes, and never the array from
     the query cache: the grid writes a sort index and a selection flag onto the
     rows it is handed, and sorts them in place. */

  /* A stable map from each row's cell-data array (by reference) back to the
     original row object. Scale sorts its internal copy of `data` in place, so
     `rowIndex` in the scaleEdit event refers to Scale's sorted order, not to
     `visible`. Keying by the cell-data array reference — which Scale preserves
     even after sorting — lets onScaleEdit recover the original row regardless
     of the current sort order. */
  const rowByData = useRef(new Map<unknown[], T>());

  const data = useMemo(() => {
    rowByData.current = new Map<unknown[], T>();
    return visible.map((row) => {
      const cells = columns.map((column) => cellFor(column, row));
      rowByData.current.set(cells, row);
      return cells;
    });
  }, [visible, columns]);

  /* The case id is a real anchor inside the grid's shadow root, so a plain click
     would reload the application. The event still bubbles out here, and its
     composed path reaches back through the shadow boundary to the anchor that
     was hit — enough to route it ourselves and leave modified clicks alone, so
     open-in-new-tab keeps working. */
  function onClick(event: MouseEvent<HTMLElement>): void {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const anchor = event.nativeEvent
      .composedPath()
      .find((node): node is HTMLAnchorElement => node instanceof HTMLAnchorElement);
    if (!anchor || anchor.target === '_blank') return;

    const href = anchor.getAttribute('href');
    if (href === null || !href.startsWith('/')) return;

    event.preventDefault();
    void navigate(href);
  }

  function clear(): void {
    setTerm('');
    setFilter(null);
    paging?.onOffset(0);
  }

  const narrowed = term.trim().length > 0 || filter !== null;

  /* Under `paging` the toolbar's count is the page against the whole answer, and
     it is shown whether or not the view is narrowed — "20 of 613" is the fact a
     paged table most needs to state, and the grid's own footer, which would
     otherwise have said it, is switched off below. */
  const counted = paging
    ? t('count.ofTotal', { shown: visible.length, total: paging.total })
    : t('count.ofTotal', { shown: visible.length, total: rows.length });

  const toolbar =
    controls || search || (filters && filters.length > 0) ? (
      <div className={styles.toolbar}>
        {controls}
        {search ? (
          <div className={styles.searchBox}>
            <ScaleTextField
              label={placeholder}
              hideLabelVisually
              placeholder={placeholder}
              inputModeType="search"
              styles={searchFieldStyles}
              value={term}
              onScaleChange={(event) => {
                setTerm(String(event.detail.value ?? ''));
                /* Back to the first page on every keystroke. Narrowing while
                   standing on page four of the old answer would land on page
                   four of the new one, which is usually past its end and always
                   somewhere nobody asked to be. */
                paging?.onOffset(0);
              }}
            />
          </div>
        ) : null}

        {filters && filters.length > 0 ? (
          <div className={styles.chips} role="radiogroup" aria-label={t('grid.filterRows')}>
            {[{ key: null, label: t('grid.all') }, ...filters].map((candidate) => {
              const on = filter === candidate.key;
              return (
                <ScaleChip
                  key={candidate.key ?? 'all'}
                  selected={on}
                  ariaRoleTitle="radio"
                  styles={chipStyles}
                  onScaleChange={(event) => {
                    /* The chip flips its own `selected` before it tells us, so
                       put it back the way React last rendered it and let the
                       state below decide. Without this, clicking the scope that
                       is already active turns it off in the DOM while React
                       still believes it is on, and the two never resync. */
                    (event.target as HTMLElement & { selected: boolean }).selected = on;
                    setFilter(on ? null : candidate.key);
                  }}
                >
                  {candidate.label}
                </ScaleChip>
              );
            })}
          </div>
        ) : null}

        {/* Counting only when the view is narrowed: an unfiltered grid already
            says how many rows it holds, in its own pager. Under `paging` there
            is no such pager, so the count stands in for it. */}
        {narrowed || paging ? <span className={styles.count}>{counted}</span> : null}
      </div>
    ) : null;

  if (data.length === 0) {
    return (
      <section aria-label={caption}>
        {toolbar ? <div className={styles.toolbarBlock}>{toolbar}</div> : null}
        <div className={styles.empty}>
          <b>{narrowed ? t('grid.noMatchTitle') : nothingTitle}</b>
          <span>{narrowed ? t('grid.noMatch') : nothingBody}</span>
          {narrowed ? (
            <button type="button" className={styles.clear} onClick={clear}>
              {t('grid.clear')}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  function onScaleEdit(event: CustomEvent<{ rows: unknown[][]; rowIndex: number; columnIndex: number; value: unknown }>): void {
    const { rows: scaleRows, rowIndex, columnIndex, value } = event.detail;
    const column = columns[columnIndex];
    if (column?.kind !== 'checkbox' || !column.onChange) return;
    /* Scale sorts its internal `rows` array in place, so `scaleRows[rowIndex]`
       is the cell-data array for the toggled row in its current sorted order.
       Look it up in the ref map to get back to the original typed row. */
    const cellData = scaleRows[rowIndex] as unknown[] | undefined;
    const row = cellData !== undefined ? rowByData.current.get(cellData) : undefined;
    if (row !== undefined) column.onChange(row, Boolean(value));
  }

  return (
    <section aria-label={caption} onClick={onClick}>
      <ScaleDataGrid
        fields={fields}
        rows={data}
        pageSize={pageSize}
        hideBorder
        freezeHeader
        styles={density}
        /* The title block reserves 72px and only gives it back when the grid has
           neither a heading nor a settings menu. With no toolbar to slot and no
           hidden column for the menu to reveal, that band holds nothing at all. */
        hideMenu={toolbar === null && !columns.some((column) => column.visible === false)}
        /* The grid's footer counts and pages the rows it holds, which under
           `paging` is one page — so it would report "20 of 20" beneath a table
           of six hundred and offer a pager that goes nowhere. Off, and replaced
           below by one that steps the offset the service reads. */
        hideInfo={paging !== undefined}
        onScaleEdit={onScaleEdit as never}
      >
        {toolbar}
      </ScaleDataGrid>
      {paging ? (
        <nav className={styles.pager} aria-label={t('grid.pager')}>
          <ScalePagination
            hideBorder
            pageSize={paging.limit}
            startElement={paging.offset}
            totalElements={paging.total}
            ariaLabelFirstPage={t('grid.firstPage')}
            ariaLabelPreviousPage={t('grid.previousPage')}
            ariaLabelNextPage={t('grid.nextPage')}
            ariaLabelLastPage={t('grid.lastPage')}
            onScalePagination={(event) => {
              if (typeof event.detail.startElement === 'number') {
                paging.onOffset(event.detail.startElement);
              }
            }}
          />
        </nav>
      ) : null}
    </section>
  );
}
